
(function () {
  const DEBOUNCE_MS = 260;
  const WAREHOUSE_LIMIT = 50;

  const debounce = (fn, wait) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };

  class NpSelector {
    constructor(root) {
      this.root = root;
      this.endpoint = (root.dataset.endpoint || '').trim();
      this.minChars = parseInt(root.dataset.minChars, 10) || 2;
      this.msg = {
        searching: root.dataset.msgSearching || 'Шукаємо…',
        noResults: root.dataset.msgNoResults || 'Нічого не знайдено',
        error: root.dataset.msgError || 'Помилка звʼязку.',
        notLive: root.dataset.msgNotLive || '',
      };
      this.note = root.querySelector('[data-gm-np-note]');

      this.city = this.buildField('city');
      this.branch = this.buildField('branch');
      this.branchTypeRef = root.querySelector('[data-gm-np-ref="branch-type"]');

      this.state = {
        cityRef: this.city.ref ? this.city.ref.value : '',
        branchRef: this.branch.ref ? this.branch.ref.value : '',
        branchType: this.branchTypeRef ? this.branchTypeRef.value : '',
      };

      root.gmNpSelector = this;

      if (!this.endpoint) {
        this.enterManualMode();
        return;
      }

      this.wireField(this.city, () => this.onCityQuery());
      this.wireField(this.branch, () => this.onBranchQuery());

      this._onDocClick = (e) => {
        if (!root.isConnected || !root.contains(e.target)) this.closeAll();
      };
      document.addEventListener('click', this._onDocClick);
    }

    buildField(kind) {
      return {
        kind,
        input: this.root.querySelector(`[data-gm-np-input="${kind}"]`),
        ref: this.root.querySelector(`[data-gm-np-ref="${kind}"]`),
        list: this.root.querySelector(`[data-gm-np-list="${kind}"]`),
        activeIndex: -1,
        items: [],
      };
    }

    enterManualMode() {
      this.branch.input.disabled = false;
      if (!this.branch.input.value) this.branch.input.placeholder = 'Напр.: Відділення №1, вул. ...';
      if (!this.city.input.value) this.city.input.placeholder = 'Напр.: Київ';
      if (this.note && this.msg.notLive) {
        this.note.textContent = this.msg.notLive;
        this.note.hidden = false;
      }
      [this.city, this.branch].forEach((f) => {
        f.input.addEventListener('input', () => {
          if (f.ref) f.ref.value = '';
          this.emitChange();
        });
      });
    }

    wireField(field, onQuery) {
      const debounced = debounce(onQuery, DEBOUNCE_MS);
      field.input.addEventListener('input', () => {
        if (field.kind === 'city') this.invalidateCity();
        else this.invalidateBranch();
        debounced();
      });
      field.input.addEventListener('focus', () => {
        if (field.items.length) this.openList(field);
      });
      field.input.addEventListener('keydown', (e) => this.onKeyDown(e, field));
    }

    onKeyDown(e, field) {
      if (field.list.hidden || !field.items.length) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          if (field.kind === 'city') this.onCityQuery();
          else this.onBranchQuery();
        }
        return;
      }
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          this.moveActive(field, 1);
          break;
        case 'ArrowUp':
          e.preventDefault();
          this.moveActive(field, -1);
          break;
        case 'Enter':
          if (field.activeIndex >= 0) {
            e.preventDefault();
            this.choose(field, field.items[field.activeIndex]);
          }
          break;
        case 'Escape':
          this.closeList(field);
          break;
        default:
          break;
      }
    }

    moveActive(field, delta) {
      const next = field.activeIndex + delta;
      field.activeIndex = Math.max(0, Math.min(field.items.length - 1, next));
      let activeId = '';
      Array.from(field.list.children).forEach((li, i) => {
        const on = i === field.activeIndex;
        li.classList.toggle('is-active', on);
        li.setAttribute('aria-selected', on ? 'true' : 'false');
        if (on) {
          activeId = li.id;
          li.scrollIntoView({ block: 'nearest' });
        }
      });
      if (activeId) field.input.setAttribute('aria-activedescendant', activeId);
      else field.input.removeAttribute('aria-activedescendant');
    }

    async onCityQuery() {
      const q = this.city.input.value.trim();
      if (q.length < this.minChars) {
        this.closeList(this.city);
        return;
      }
      this.showNote(this.msg.searching);
      try {
        const items = await this.fetchProxy({ action: 'cities', query: q });
        this.renderList(this.city, items, (it) => (it.area ? `${it.label} · ${it.area}` : it.label));
        this.hideNote();
      } catch (err) {
        this.renderList(this.city, []);
        this.showNote(this.msg.error);
      }
    }

    async onBranchQuery() {
      if (!this.state.cityRef) return;
      const q = this.branch.input.value.trim();
      this.showNote(this.msg.searching);
      try {
        const items = await this.fetchProxy({
          action: 'warehouses',
          cityRef: this.state.cityRef,
          query: q,
          limit: WAREHOUSE_LIMIT,
        });
        this.renderList(this.branch, items, (it) => it.label);
        this.hideNote();
      } catch (err) {
        this.renderList(this.branch, []);
        this.showNote(this.msg.error);
      }
    }

    async fetchProxy(payload) {
      const res = await fetch(this.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data || data.ok === false) {
        throw new Error((data && data.error) || `HTTP ${res.status}`);
      }
      return Array.isArray(data.items) ? data.items : [];
    }

    renderList(field, items, labelFn) {
      field.items = items;
      field.activeIndex = -1;
      field.list.innerHTML = '';

      if (!items.length) {
        const li = document.createElement('li');
        li.className = 'gm-np__option gm-np__option--empty';
        li.setAttribute('role', 'presentation');
        li.textContent = this.msg.noResults;
        field.list.appendChild(li);
        this.openList(field);
        return;
      }

      items.forEach((it, i) => {
        const li = document.createElement('li');
        li.className = 'gm-np__option';
        li.id = `${field.list.id}-opt-${i}`;
        li.setAttribute('role', 'option');
        li.setAttribute('aria-selected', 'false');
        li.textContent = labelFn ? labelFn(it) : it.label;
        if (field.kind === 'branch' && it.type === 'postomat') {
          const tag = document.createElement('span');
          tag.className = 'gm-np__tag';
          tag.textContent = 'поштомат';
          li.appendChild(tag);
        }
        li.addEventListener('mousedown', (e) => {
          e.preventDefault();
          this.choose(field, it);
        });
        field.list.appendChild(li);
      });
      this.openList(field);
    }

    openList(field) {
      field.list.hidden = false;
      field.input.setAttribute('aria-expanded', 'true');
    }

    closeList(field) {
      field.list.hidden = true;
      field.activeIndex = -1;
      field.input.setAttribute('aria-expanded', 'false');
      field.input.removeAttribute('aria-activedescendant');
    }

    closeAll() {
      this.closeList(this.city);
      this.closeList(this.branch);
    }

    choose(field, item) {
      if (field.kind === 'city') {
        this.state.cityRef = item.ref || '';
        this.city.input.value = item.label || '';
        if (this.city.ref) this.city.ref.value = this.state.cityRef;

        this.invalidateBranch({ skipEmit: true });
        this.branch.input.value = '';
        this.branch.input.disabled = false;
        this.branch.input.placeholder = 'Введіть номер або вулицю';
        this.closeList(this.city);
        this.branch.input.focus();
      } else {
        this.state.branchRef = item.ref || '';
        this.state.branchType = item.type || 'branch';
        this.branch.input.value = item.label || '';
        if (this.branch.ref) this.branch.ref.value = this.state.branchRef;
        if (this.branchTypeRef) this.branchTypeRef.value = this.state.branchType;
        this.closeList(this.branch);
      }
      this.emitChange();
    }

    invalidateCity() {
      this.state.cityRef = '';
      if (this.city.ref) this.city.ref.value = '';
      this.invalidateBranch({ skipEmit: true });
    }

    invalidateBranch(opts) {
      this.state.branchRef = '';
      this.state.branchType = '';
      if (this.branch.ref) this.branch.ref.value = '';
      if (this.branchTypeRef) this.branchTypeRef.value = '';
      if (!(opts && opts.skipEmit)) this.emitChange();
    }

    values() {
      return {
        city: this.city.input.value.trim(),
        cityRef: this.state.cityRef || '',
        branch: this.branch.input.value.trim(),
        branchRef: this.state.branchRef || '',
        branchType: this.state.branchType || '',
      };
    }

    isComplete() {
      const v = this.values();
      if (!this.endpoint) return v.city.length > 1 && v.branch.length > 1;
      return Boolean(v.cityRef && v.branchRef);
    }

    emitChange() {
      this.root.dispatchEvent(new CustomEvent('gm:np:change', { bubbles: true, detail: this.values() }));
    }

    showNote(text) {
      if (!this.note) return;
      this.note.textContent = text;
      this.note.hidden = false;
    }

    hideNote() {
      if (!this.note || (this.msg.notLive && this.note.textContent === this.msg.notLive)) return;
      this.note.hidden = true;
    }
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-gm-np-root]:not([data-gm-np-bound])').forEach((root) => {
      root.setAttribute('data-gm-np-bound', '1');
      new NpSelector(root);
    });
  }

  initAll(document);

  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.addedNodes && m.addedNodes.length) {
        initAll(document);
        return;
      }
    }
  });
  if (document.body) observer.observe(document.body, { childList: true, subtree: true });
})();
